import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "@/lib/firebase";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { apiClient, setApiToken } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";

interface InviteDetails {
  email: string;
  companyName: string;
}

interface UseInviteAcceptanceProps {
  token: string | undefined;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export function useInviteAcceptance({ token, onSuccess, onError }: UseInviteAcceptanceProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);

  // Fetch invite details on mount
  useEffect(() => {
    const fetchInviteDetails = async () => {
      if (!token) {
        setStatus("error");
        setErrorMessage("Invalid invitation link");
        onError?.("Invalid invitation link");
        return;
      }

      // Don't re-fetch if we already have details or are in a terminal state
      if (inviteDetails || status === "success" || status === "error") {
        return;
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/api/invites/details/${token}`
        );
        
        if (!response.ok) {
          // Handle expired or invalid invitations
          if (response.status === 400) {
            const errorData = await response.json().catch(() => ({})) as { code?: string; message?: string };
            if (errorData.code === 'INVITE_EXPIRED' || errorData.message?.toLowerCase().includes('expired')) {
              const msg = "This invitation has expired. Please contact your company administrator to request a new invitation.";
              setStatus("error");
              setErrorMessage(msg);
              onError?.(msg);
              return;
            }
          } else if (response.status === 404) {
            const msg = "This invitation link is invalid or has expired.";
            setStatus("error");
            setErrorMessage(msg);
            onError?.(msg);
            return;
          } else if (response.status === 409) {
            const msg = "This invitation has already been accepted.";
            setStatus("error");
            setErrorMessage(msg);
            onError?.(msg);
            return;
          }
          
          console.error("Failed to fetch invitation details");
          return;
        }
        
        const data = await response.json() as InviteDetails;
        
        console.log('[useInviteAcceptance] Invitation details:', { 
          email: data.email, 
          companyName: data.companyName
        });
        
        setInviteDetails(data);
      } catch (error) {
        console.error("Failed to fetch invitation details:", error);
      }
    };

    fetchInviteDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const acceptInvitation = useCallback(async () => {
    if (!token) return;

    try {
      setStatus("loading");
      
      await apiClient.companies.acceptCompanyInvite({ 
        token
      });
      
      setStatus("success");
      
      // Clear the pending invite token from session storage
      sessionStorage.removeItem("pendingInviteToken");
      
      // Invalidate queries to refresh team members list and user data
      queryClient.invalidateQueries({ queryKey: ["/api/users/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies/:companyId/members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies/:companyId/invites"] });
      
      toast({
        title: "Invitation Accepted",
        description: "You have successfully joined the company!",
      });
      
      onSuccess?.();
    } catch (error: unknown) {
      setStatus("error");
      
      const err = error as { response?: { status?: number; data?: { message?: string; error?: string; code?: string } } };
      
      let displayMessage = "";
      
      if (err.response?.status === 404) {
        displayMessage = "This invitation link is invalid or has expired.";
      } else if (err.response?.status === 400 && 
                 (err.response?.data?.code === 'INVITE_EXPIRED' || 
                  err.response?.data?.message?.toLowerCase().includes('expired'))) {
        displayMessage = "This invitation has expired. Please contact your company administrator to request a new invitation.";
      } else if (err.response?.status === 409) {
        displayMessage = "You have already accepted this invitation or are already a member of this company.";
      } else if (err.response?.status === 403) {
        displayMessage = err.response?.data?.message || "This invitation was sent to a different email address.";
      } else {
        displayMessage = err.response?.data?.message || "Failed to accept invitation. Please try again.";
      }
      
      setErrorMessage(displayMessage);
      onError?.(displayMessage);
      
      toast({
        title: "Error",
        description: displayMessage,
        variant: "destructive",
      });
    }
  }, [token, toast, queryClient, onSuccess, onError]);

  /**
   * Handle signup and invitation acceptance in one flow
   * 
   * This flow is different from the normal signup process:
   * 
   * Normal signup flow (use-auth.ts signupMutation):
   * 1. Create Firebase account
   * 2. Create new company in database
   * 3. Create user record linked to new company
   * 
   * Invitation signup flow (this function):
   * 1. Create Firebase account only (no company creation)
   * 2. Call acceptInvitation() which:
   *    - Validates the invite token
   *    - Creates user record linked to EXISTING company from invitation
   *    - Sets user role based on invitation (admin/member)
   * 
   * Flow sequence:
   * 1. createUserWithEmailAndPassword() - Creates Firebase auth account
   * 2. updateProfile() - Sets display name in Firebase
   * 3. getIdToken() - Gets JWT token for API authentication
   * 4. setApiToken() - Configures API client with token
   * 5. acceptInvitation() - Backend creates user record with invited company/role
   */
  const handleSignupAndAccept = useCallback(async (data: SignupData) => {
    if (!token) return;

    try {
      setStatus("loading");
      
      // Step 1: Create Firebase authentication account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      // Step 2: Set the user's display name in Firebase
      await updateProfile(userCredential.user, {
        displayName: `${data.firstName} ${data.lastName}`.trim()
      });

      // Step 3: Get authentication token for API requests
      const authToken = await userCredential.user.getIdToken(true);
      setApiToken(authToken);

      toast({
        title: "Account Created",
        description: "Accepting invitation...",
      });

      // Step 4: Accept the invitation
      await acceptInvitation();
      
    } catch (error) {
      setStatus("error");
      const errorMsg = error instanceof Error ? error.message : "Failed to create account";
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
      toast({
        title: "Signup failed",
        description: errorMsg,
        variant: "destructive",
      });
    }
  }, [token, toast, acceptInvitation, onError]);

  return {
    status,
    errorMessage,
    inviteDetails,
    handleSignupAndAccept,
    acceptInvitation,
  };
}
